/**
 * Calendar Page - Owner Dashboard Main View
 * 
 * Purpose:
 * Primary calendar view for property owners showing reservations, availability, and key metrics.
 * Displays a month-view calendar with booking details, KPI cards (nights booked, occupancy, payout),
 * upcoming events snapshot, and tourist tax information. Supports both desktop and mobile responsive
 * layouts with mobile-specific navigation and day/week detail views.
 * 
 * High-level Workflow:
 * 1. On mount: Loads property data from SessionContext, detects mobile viewport, initializes calendar
 *    to current month, sets up performance tracking and scroll-based header docking.
 * 2. Property selection: When a property is selected, fetches reservations, calendar metrics, availability,
 *    and next 7 days snapshot for the selected month. All fetches are gated by property availability
 *    and include race condition mitigation to prevent duplicate API calls.
 * 3. Month navigation: When user changes month, recalculates month bounds and refetches data for new month.
 *    Uses month-scoped date ranges (month start to month end) with widened fetch range (30 days before
 *    to 1 day after) to capture reservations that span month boundaries.
 * 4. Data transformation: Maps raw reservation data to calendar event format, filters by status (excludes
 *    cancelled/inquiry), filters by month overlap, calculates occupied nights and tourist tax totals.
 * 5. Rendering: Conditionally renders desktop vs mobile layouts, shows loading skeletons during fetches,
 *    displays error states if fetches fail, renders calendar grid with bookings and availability overlays.
 * 6. User interactions: Handles booking clicks (opens detail modal), date clicks (mobile: shows week/day
 *    views), month navigation, property switching, modal open/close, calendar collapse/expand.
 * 
 * Assumptions & Invariants:
 * - Date Handling: All dates use parseLocalDate() utility to avoid timezone shifting bugs. The native
 *   JavaScript Date constructor with "YYYY-MM-DD" strings parses as UTC midnight, causing timezone
 *   shifts. parseLocalDate() ensures dates are parsed in local timezone.
 * - API Response Shapes: API responses may be wrapped ({ data: {...}, outcome: 'success' }) or flat
 *   ({ ...data..., outcome: 'success' }). normalizeApiPayload() handles both formats consistently.
 * - Fetch Gating: All data fetches are gated by three conditions: listingId exists, propertiesLoading
 *   is false, and selectedProperty object exists. This prevents race conditions where listingId becomes
 *   available before properties fetch completes or auth is ready.
 * - Race Condition Mitigation: Uses inFlightFetchRef and lastFetchedKeyRef with month-key format
 *   "${listingId}-${year}-${month}" to prevent duplicate fetches. Synchronous guards check for
 *   in-flight fetches before starting async work. AbortController cancels in-flight requests on property
 *   switch or unmount.
 * - Mobile Breakpoint: Mobile detection uses window.innerWidth <= 768px. This breakpoint is assumed
 *   to match CSS media queries and design system breakpoints. Mobile layout shows command bar, week
 *   strip, and day details instead of full month grid.
 * - Property Selection: selectedProperty object from SessionContext is authoritative. If selectedProperty
 *   is null, no data fetches occur even if listingId exists (prevents stale data from previous property).
 * - Occupied Nights Calculation: Checkout day is NOT counted as occupied (half-open interval [checkin,
 *   checkout)). This matches business logic where checkout day is available for next guest.
 * - Status Filtering: Only BOOKED and OWNER_STAY status buckets are shown on calendar. CANCELLED and
 *   INQUIRY are excluded by default.
 * 
 * Dependencies:
 * - Internal:
 *   - contexts/SessionContext.jsx: Authentication, property selection, owner/property data
 *   - contexts/ThemeContext.jsx: Theme management for styling
 *   - components/owner/BookingCalendar.jsx: Main calendar grid component
 *   - components/owner/UpcomingEventsSnapshot.jsx: Next 7 days events display
 *   - components/owner/BookingDetailModal.jsx: Booking details modal dialog
 *   - components/owner/PropertyOverviewModal.jsx: Property overview modal
 *   - components/owner/MobileWeekStrip.jsx: Mobile week view navigation
 *   - components/owner/MobileDayDetails.jsx: Mobile day detail cards
 *   - components/owner/MiniAgenda.jsx: Mini agenda component
 *   - components/owner/CalendarLegendSheet.jsx: Calendar legend bottom sheet
 *   - components/owner/PropertyLinksMenu.jsx: Property external links menu
 *   - components/owner/BottomSheet.jsx: Mobile bottom sheet container
 *   - components/shared/SkeletonLoader.jsx: Loading skeleton components
 *   - components/shared/ContextBar.jsx: Context bar component
 *   - components/shared/KPICardRow.jsx: KPI cards row display
 *   - components/shared/MonthSelector.jsx: Month selection dropdown
 *   - services/ownerApi.js: API client for owner portal endpoints
 *   - utils/sanitizeForOwnerView.js: Data sanitization for owner view mode
 *   - utils/dateRange.js: Date range utilities (overlapsRange)
 *   - utils/hostawayStatus.js: Status bucket classification
 *   - utils/ownerReservationsMapper.js: Reservation to UI format mapping
 *   - utils/parseLocalDate.js: Local timezone date parsing
 *   - utils/calendarCalculations.js: Month bounds and occupied nights calculations
 *   - utils/calendarFormatting.js: Currency and nights booked formatting
 *   - utils/calendarNormalization.js: API response payload normalization
 *   - styles/calendar-responsive.css: Responsive styles and animations
 * - External:
 *   - react: React hooks (useState, useEffect, useMemo, useRef)
 *   - react-router-dom: Navigation (useNavigate)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Phase 0-1 refactoring - Added comprehensive file header documentation and extracted
 *   pure utility functions (getMonthBounds, calculateOccupiedNightsInMonth, formatCurrency,
 *   formatNightsBooked, normalizeApiPayload) to utils/calendarCalculations.js, utils/calendarFormatting.js,
 *   and utils/calendarNormalization.js. No behavior changes.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BookingCalendar from '../../components/owner/BookingCalendar.jsx';
import UpcomingEventsSnapshot from '../../components/owner/UpcomingEventsSnapshot.jsx';
import BookingDetailModal from '../../components/owner/BookingDetailModal.jsx';
import PropertyOverviewModal from '../../components/owner/PropertyOverviewModal.jsx';
import OwnerStayBookingModal from '../../components/owner/OwnerStayBookingModal.jsx';
// PHASE 8: Multi-mode mobile calendar components
import MobileWeekStrip from '../../components/owner/MobileWeekStrip.jsx';
import MobileDayDetails from '../../components/owner/MobileDayDetails.jsx';
import MiniAgenda from '../../components/owner/MiniAgenda.jsx';
import { useSession } from '../../contexts/SessionContext.jsx';
import { SkeletonCard, SkeletonText } from '../../components/shared/SkeletonLoader.jsx';
import ContextBar from '../../components/shared/ContextBar.jsx';
import KPICardRow from '../../components/shared/KPICardRow.jsx';
import MonthSelector from '../../components/shared/MonthSelector.jsx';
import { sanitizeForOwnerView } from '../../utils/sanitizeForOwnerView.js';
import { normalizeBookingForModal } from '../../utils/bookingDetailModalUtils.js';
import PropertyLinksMenu from '../../components/owner/PropertyLinksMenu.jsx';
import { overlapsRange } from '../../utils/dateRange.js';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import {
  getStatusBucket,
} from '../../utils/hostawayStatus.js';
// API imports moved to hooks (Phase 2)
import { mapReservationsToUI, reservationToCalendarEvent } from '../../utils/ownerReservationsMapper.js';
import { parseLocalDate } from '../../utils/parseLocalDate.js';
import { getMonthBounds } from '../../utils/calendarCalculations.js';
import { computeCalendarMonthSummary } from '../../utils/calendarMonthSummary.js';
import { formatCurrency, formatNightsBooked } from '../../utils/calendarFormatting.jsx';
import { normalizeApiPayload } from '../../utils/calendarNormalization.js';
import { useCalendarReservations } from '../../hooks/useCalendarReservations.js';
import { useCalendarAvailability } from '../../hooks/useCalendarAvailability.js';
import { useNext7DaysSnapshot } from '../../hooks/useNext7DaysSnapshot.js';
import { useCalendarUIState } from '../../hooks/useCalendarUIState.js';
import { useReservationYears } from '../../hooks/useReservationYears.js';
import { cancelOwnerStay, createOwnerStay, updateOwnerStay } from '../../services/ownerApi.js';
import CalendarPageGridWithPerf from '../../components/owner/CalendarPageGridWithPerf.jsx';
import CalendarPageHeader from '../../components/owner/CalendarPageHeader.jsx';
import CalendarPageMobileCommandBar from '../../components/owner/CalendarPageMobileCommandBar.jsx';
import CalendarPageKPICards from '../../components/owner/CalendarPageKPICards.jsx';
import CalendarPageTouristTaxBanner from '../../components/owner/CalendarPageTouristTaxBanner.jsx';
import CalendarPageErrorStates from '../../components/owner/CalendarPageErrorStates.jsx';
import { styles } from '../../styles/calendarPageStyles.js';
import '../../styles/calendar-responsive.css'; // PHASE 9C/9D: Mobile calendar responsive styles and animations

// Performance debug flag (from env or URL param)
const PERF_DEBUG = typeof window !== 'undefined' && (
  import.meta.env.VITE_OWNER_PERF_DEBUG === '1' ||
  new URLSearchParams(window.location.search).get('perf_debug') === '1'
);

function formatDateKey(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getOwnerStayDefaultCheckIn(selectedDate, calendarMonth) {
  if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
    return formatDateKey(selectedDate);
  }
  if (typeof selectedDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(selectedDate)) {
    return selectedDate.slice(0, 10);
  }

  const today = new Date();
  const viewingCurrentMonth =
    calendarMonth.getFullYear() === today.getFullYear() &&
    calendarMonth.getMonth() === today.getMonth();

  if (viewingCurrentMonth) {
    return formatDateKey(today);
  }

  return formatDateKey(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1));
}

function normalizeDateKey(value, fallbackDate = null) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return formatDateKey(value);
  }
  if (fallbackDate instanceof Date && !isNaN(fallbackDate.getTime())) {
    return formatDateKey(fallbackDate);
  }
  return null;
}

function dateHasBooking(bookings, dateKey) {
  if (!dateKey || !Array.isArray(bookings) || bookings.length === 0) return false;
  return bookings.some((booking) => {
    const checkIn = normalizeDateKey(booking?.check_in_date || booking?.checkin_date);
    const checkOut = normalizeDateKey(booking?.check_out_date || booking?.checkout_date);
    if (!checkIn || !checkOut) return false;
    return dateKey >= checkIn && dateKey < checkOut;
  });
}

function calculateStayNights(arrivalDate, departureDate) {
  const start = parseLocalDate(arrivalDate);
  const end = parseLocalDate(departureDate);
  if (!start || !end || end <= start) return 0;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

function getOwnerStayReservationKey(booking) {
  const value = booking?.hostaway_reservation_id || booking?.reservation_id || booking?.id || null;
  return value !== null && value !== undefined && value !== '' ? String(value) : null;
}

function getOwnerStayReservationId(booking) {
  const id = Number(getOwnerStayReservationKey(booking));
  return Number.isFinite(id) && id > 0 ? id : null;
}

function ownerStayOverrideMatchesSyncedBooking(synced, override) {
  if (!synced || !override) return false;
  return (
    String(synced.check_in_date || synced.checkin_date || '') === String(override.check_in_date || override.checkin_date || '') &&
    String(synced.check_out_date || synced.checkout_date || '') === String(override.check_out_date || override.checkout_date || '') &&
    String(synced.guest_name || '') === String(override.guest_name || '') &&
    Number(synced.guest_count || 0) === Number(override.guest_count || 0) &&
    String(synced.status || '').toLowerCase() === String(override.status || '').toLowerCase()
  );
}

function buildPendingOwnerStay({ listingId, item, form, previous = null }) {
  const hostawayReservationId =
    item?.hostaway_reservation_id ||
    item?.reservation_id ||
    previous?.hostaway_reservation_id ||
    previous?.reservation_id ||
    previous?.id ||
    null;
  const localId = `pending-owner-stay-local-${Date.now()}`;
  const reservationId = hostawayReservationId || localId;
  return {
    ...previous,
    id: String(reservationId),
    reservation_id: reservationId,
    hostaway_reservation_id: hostawayReservationId,
    client_pending_key: localId,
    listing_id: Number(listingId),
    check_in_date: form.arrivalDate,
    check_out_date: form.departureDate,
    checkin_date: form.arrivalDate,
    checkout_date: form.departureDate,
    status: 'ownerStay',
    channel: 'Hostaway Direct',
    channel_code: 'Hostaway Direct',
    guest_name: form.ownerName,
    email: form.email,
    phone: form.phone,
    guest_count: Number(form.numberOfGuests) || 1,
    number_of_guests: Number(form.numberOfGuests) || 1,
    note: form.note,
    nights: calculateStayNights(form.arrivalDate, form.departureDate),
    total_price: 0,
    currency: 'USD',
    pending_sync: true,
    owner_portal_created: true,
    canonical_financial_summary: {
      master_channel_key: 'owner',
      master_channel_label: 'Owner',
      channel_subtype: 'owner_stay',
    },
  };
}

function getOwnerAccountDefaults(user) {
  if (!user || typeof user !== 'object') {
    return {
      ownerName: '',
      email: '',
      phone: '',
    };
  }

  return {
    ownerName: user.display_name || user.displayName || user.name || '',
    email: user.email || '',
    phone: user.phone || user.phone_number || user.phoneNumber || '',
  };
}

// Note: Previously used getDefaultDateRange() for wide date ranges
// Now using month-scoped dates based on selected month (monthStart/monthEnd)
// This ensures bounded API payloads while allowing navigation to any historical month

export default function CalendarPage({ onLogout }) {
  const { effectiveMode, selectedOwnerId, selectedPropertyId, selectedProperty, propertiesLoading, propertiesError, availableProperties, setSelectedListingId, retryProperties, currentUser } = useSession();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const ownerAccountDefaults = useMemo(
    () => getOwnerAccountDefaults(currentUser),
    [currentUser]
  );
  
  // Memoize property display values to avoid recalculations
  const propertyDisplayValues = useMemo(() => {
    if (!selectedProperty) {
      return {
        shortName: 'Property',
        hero: null,
        thumb: null,
      };
    }
    // Get property display name (display_name > clc_short_name > property_name/name)
    // NEVER use contact_name as fallback
    const getPropertyShortName = (property) => {
      return property?.display_name || property?.clc_short_name || property?.name || property?.property_name || 'Unnamed Property';
    };
    
    // Get property hero image for header banner (hero_url only, not thumb)
    const getPropertyHero = (property) => {
      return property?.hero_url || null;
    };
    
    // Get property thumbnail for avatar/picker (thumb_url only, curated-only contract)
    // NEVER use Hostaway thumbnail_url/image_url as fallback
    const getPropertyThumb = (property) => {
      return property?.thumb_url || null;
    };
    
    return {
      shortName: getPropertyShortName(selectedProperty),
      hero: getPropertyHero(selectedProperty),
      thumb: getPropertyThumb(selectedProperty),
    };
  }, [selectedProperty]);

  // UI state hook (Phase 3 extraction)
  const uiState = useCalendarUIState({ 
    selectedPropertyId,
    propertyHero: propertyDisplayValues.hero,
  });
  
  // Extract UI state and handlers from hook
  const {
    selectedBooking,
    setSelectedBooking,
    isModalOpen,
    setIsModalOpen,
    isPropertyOverviewOpen,
    setIsPropertyOverviewOpen,
    selectedDate,
    setSelectedDate,
    selectedWeekStart,
    setSelectedWeekStart,
    dayDetailsExpanded,
    setDayDetailsExpanded,
    isMobile,
    calendarView,
    setCalendarView,
    isKPIsExpanded,
    setIsKPIsExpanded,
    calendarMonth,
    setCalendarMonth,
    eventFilter,
    setEventFilter,
    heroImageReady,
    setHeroImageReady,
    isDocked,
    setIsDocked,
    isCalendarCollapsed,
    setIsCalendarCollapsed,
    toggleCalendarCollapse,
    showPropertiesTimeout,
    setShowPropertiesTimeout,
    mountTimeRef,
    heroImageRef,
    dockSentinelRef,
    gridRenderTimeRef,
    handleBookingClick,
    handleCloseModal,
    handleDateClick,
    handlePrevMonth,
    handleNextMonth,
    handleToday,
  } = uiState;

  const [isOwnerStayModalOpen, setIsOwnerStayModalOpen] = useState(false);
  const [ownerStayModalMode, setOwnerStayModalMode] = useState('create');
  const [ownerStayEditBooking, setOwnerStayEditBooking] = useState(null);
  const [ownerStayDefaultCheckInDate, setOwnerStayDefaultCheckInDate] = useState(null);
  const [pendingOwnerStays, setPendingOwnerStays] = useState([]);
  const [ownerStayLocalChanges, setOwnerStayLocalChanges] = useState({});
  
  // Timeout escape hatch: if propertiesLoading is true for more than 10 seconds, show retry option
  useEffect(() => {
    if (!propertiesLoading) {
      setShowPropertiesTimeout(false);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      setShowPropertiesTimeout(true);
    }, 10000); // 10 seconds
    
    return () => clearTimeout(timeoutId);
  }, [propertiesLoading, setShowPropertiesTimeout]);

  // Calculate month bounds from selected month
  const { monthStart, monthEnd } = useMemo(() => getMonthBounds(calendarMonth), [calendarMonth]);
  
  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';
  const debugEnabled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  // Stabilize listing_id to prevent unnecessary re-renders
  // Extract primitive value to avoid object reference instability
  const listingId = useMemo(() => {
    const id = selectedProperty?.listing_id;
    if (!id) return null;
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    return isNaN(numId) || numId <= 0 ? null : numId;
  }, [selectedProperty?.listing_id]);

  const defaultOwnerStayCheckIn = useMemo(
    () => ownerStayDefaultCheckInDate || getOwnerStayDefaultCheckIn(selectedDate, calendarMonth),
    [ownerStayDefaultCheckInDate, selectedDate, calendarMonth]
  );

  const handleOpenOwnerStayModal = useCallback(() => {
    setOwnerStayModalMode('create');
    setOwnerStayEditBooking(null);
    setOwnerStayDefaultCheckInDate(null);
    setIsOwnerStayModalOpen(true);
  }, []);

  const handleOwnerStaySubmit = useCallback(async (form) => {
    if (ownerStayModalMode === 'edit') {
      const reservationId = getOwnerStayReservationId(ownerStayEditBooking);
      if (!reservationId) {
        throw new Error('Owner stay reservation id is missing.');
      }

      const response = await updateOwnerStay({
        reservationId,
        listingId: ownerStayEditBooking?.listing_id || listingId,
        arrivalDate: form.arrivalDate,
        departureDate: form.departureDate,
        ownerName: form.ownerName,
        email: form.email,
        phone: form.phone,
        numberOfGuests: form.numberOfGuests,
        note: form.note,
      });

      const item = response?.item || response?.data?.item || null;
      const updatedStay = buildPendingOwnerStay({
        listingId: ownerStayEditBooking?.listing_id || listingId,
        item,
        form,
        previous: ownerStayEditBooking,
      });
      const key = getOwnerStayReservationKey(updatedStay);

      if (key) {
        setPendingOwnerStays((current) => current.map((stay) => (
          getOwnerStayReservationKey(stay) === key ? updatedStay : stay
        )));
        setOwnerStayLocalChanges((current) => ({
          ...current,
          [key]: {
            type: 'upsert',
            booking: updatedStay,
          },
        }));
      }

      const checkInDate = parseLocalDate(form.arrivalDate);
      if (checkInDate && (
        checkInDate.getFullYear() !== calendarMonth.getFullYear() ||
        checkInDate.getMonth() !== calendarMonth.getMonth()
      )) {
        setCalendarMonth(new Date(checkInDate.getFullYear(), checkInDate.getMonth(), 1));
      }

      setIsOwnerStayModalOpen(false);
      setOwnerStayModalMode('create');
      setOwnerStayEditBooking(null);
      setOwnerStayDefaultCheckInDate(null);
      return;
    }

    const response = await createOwnerStay({
      listingId,
      arrivalDate: form.arrivalDate,
      departureDate: form.departureDate,
      ownerName: form.ownerName,
      email: form.email,
      phone: form.phone,
      numberOfGuests: form.numberOfGuests,
      note: form.note,
    });

    const item = response?.item || response?.data?.item || null;
    const pendingStay = buildPendingOwnerStay({ listingId, item, form });
    setPendingOwnerStays((current) => {
      const hostawayId = pendingStay.hostaway_reservation_id ? String(pendingStay.hostaway_reservation_id) : null;
      const withoutDuplicate = hostawayId
        ? current.filter((stay) => String(stay.hostaway_reservation_id || '') !== hostawayId)
        : current;
      return [...withoutDuplicate, pendingStay];
    });

    const checkInDate = parseLocalDate(form.arrivalDate);
    if (checkInDate && (
      checkInDate.getFullYear() !== calendarMonth.getFullYear() ||
      checkInDate.getMonth() !== calendarMonth.getMonth()
    )) {
      setCalendarMonth(new Date(checkInDate.getFullYear(), checkInDate.getMonth(), 1));
    }

    setIsOwnerStayModalOpen(false);
    setOwnerStayDefaultCheckInDate(null);
  }, [calendarMonth, listingId, ownerStayEditBooking, ownerStayModalMode, setCalendarMonth]);

  const handleOwnerStayModalClose = useCallback(() => {
    setIsOwnerStayModalOpen(false);
    setOwnerStayModalMode('create');
    setOwnerStayEditBooking(null);
    setOwnerStayDefaultCheckInDate(null);
  }, []);

  const handleEditOwnerStay = useCallback((booking) => {
    const reservationId = getOwnerStayReservationId(booking);
    if (!reservationId) return;
    setOwnerStayModalMode('edit');
    setOwnerStayEditBooking(booking);
    setOwnerStayDefaultCheckInDate(null);
    setIsModalOpen(false);
    setSelectedBooking(null);
    setIsOwnerStayModalOpen(true);
  }, [setIsModalOpen, setSelectedBooking]);

  const handleCancelOwnerStay = useCallback(async (booking) => {
    const reservationId = getOwnerStayReservationId(booking);
    if (!reservationId) {
      throw new Error('Owner stay reservation id is missing.');
    }

    const response = await cancelOwnerStay({
      reservationId,
      listingId: booking?.listing_id || listingId,
    });
    const item = response?.item || response?.data?.item || null;
    const key = String(item?.hostaway_reservation_id || item?.reservation_id || reservationId);

    setPendingOwnerStays((current) => current.filter((stay) => getOwnerStayReservationKey(stay) !== key));
    setOwnerStayLocalChanges((current) => ({
      ...current,
      [key]: {
        type: 'cancel',
        reservationId: key,
      },
    }));
  }, [listingId]);

  // Data fetching hooks (Phase 2 extraction). Reservations and availability run on same deps so fetches are concurrent (owner dashboard speed test forensics).
  const {
    reservations,
    reservationsMetadata,
    reservationsLoading,
    reservationsError,
    bookingsFetchCount,
  } = useCalendarReservations({
    listingId,
    calendarMonth,
    propertiesLoading,
    selectedProperty,
    monthStart,
    monthEnd,
  });

  const {
    availability,
    availabilityLoading,
    availabilityError,
  } = useCalendarAvailability({
    listingId,
    monthStart,
    monthEnd,
    propertiesLoading,
    selectedProperty,
  });

  const {
    next7DaysSnapshot,
    next7DaysSnapshotLoading,
    next7DaysSnapshotError,
  } = useNext7DaysSnapshot({
    listingId,
    propertiesLoading,
    selectedProperty,
  });

  const { years: reservationYears, loading: reservationYearsLoading, error: reservationYearsError, reservationsCount } = useReservationYears({
    listingId,
    propertiesLoading,
    selectedProperty,
  });
  
  // OLD CODE REMOVED - useEffect blocks and UI handlers extracted to hooks (Phase 2 & 3)
  // All four data-fetching useEffect blocks have been moved to custom hooks (Phase 2):
  // - useCalendarReservations (removed ~405 lines)
  // - useCalendarAvailability (removed ~63 lines)
  // - useCalendarMetrics (removed ~86 lines)
  // - useNext7DaysSnapshot (removed ~88 lines)
  // All UI state and handlers have been moved to useCalendarUIState hook (Phase 3):
  // - Modal states, date selection, mobile/responsive states, calendar navigation,
  //   display filters, UI visual states, UI refs, and all UI handlers

  // PHASE 3: Use live reservations data (mapped to calendar format)
  // Map live reservations to UI format - must be before early returns for hooks consistency
  const mappedReservations = useMemo(() => {
    if (!reservations || reservations.length === 0) return [];
    return mapReservationsToUI(reservations);
  }, [reservations]);
  
  // Convert to calendar bookings format (for compatibility with existing components)
  // PHASE 2: Add safe date parsing and error handling
  const calendar_bookings = useMemo(() => {
    if (!mappedReservations || mappedReservations.length === 0) return [];
    
    const stats = {
      totalFetched: mappedReservations.length,
      totalMapped: 0,
      totalSkipped: 0,
      skipReasons: {},
    };

    const events = mappedReservations
      .map(reservation => {
        try {
          const event = reservationToCalendarEvent(reservation);
          if (event) {
            // PHASE 3: Safe date parsing - ensure dates are valid
            // Use parseLocalDate to avoid timezone shifting bug (new Date("YYYY-MM-DD") parses as UTC midnight)
            const checkIn = event.check_in_date ? parseLocalDate(event.check_in_date) : null;
            const checkOut = event.check_out_date ? parseLocalDate(event.check_out_date) : null;
            
            // Validate dates
            if (!checkIn || isNaN(checkIn.getTime())) {
              stats.totalSkipped++;
              stats.skipReasons['invalid_checkin'] = (stats.skipReasons['invalid_checkin'] || 0) + 1;
              if (debug) {
                console.warn('[CalendarPage] Skipping reservation with invalid check_in_date:', {
                  id: reservation.reservation_id,
                  check_in_date: event.check_in_date,
                });
              }
              return null;
            }
            
            if (!checkOut || isNaN(checkOut.getTime())) {
              stats.totalSkipped++;
              stats.skipReasons['invalid_checkout'] = (stats.skipReasons['invalid_checkout'] || 0) + 1;
              if (debug) {
                console.warn('[CalendarPage] Skipping reservation with invalid check_out_date:', {
                  id: reservation.reservation_id,
                  check_out_date: event.check_out_date,
                });
              }
              return null;
            }
            
            // Ensure end date is after start date (coerce if needed)
            if (checkOut <= checkIn) {
              // Coerce: set checkout to checkin + 1 day (safest option)
              const adjustedCheckOut = new Date(checkIn);
              adjustedCheckOut.setDate(adjustedCheckOut.getDate() + 1);
              event.check_out_date = adjustedCheckOut.toISOString().split('T')[0];
              stats.skipReasons['end_before_start_coerced'] = (stats.skipReasons['end_before_start_coerced'] || 0) + 1;
              if (debug) {
                console.warn('[CalendarPage] Coerced checkout date (was before checkin):', {
                  id: reservation.reservation_id,
                  original_checkout: checkOut.toISOString(),
                  adjusted_checkout: adjustedCheckOut.toISOString(),
                });
              }
            }
            
            // Ensure event has stable id
            if (!event.reservation_id) {
              stats.totalSkipped++;
              stats.skipReasons['missing_id'] = (stats.skipReasons['missing_id'] || 0) + 1;
              if (debug) {
                console.warn('[CalendarPage] Skipping reservation with missing reservation_id:', reservation);
              }
              return null;
            }
            
            stats.totalMapped++;
            return event;
          } else {
            stats.totalSkipped++;
            stats.skipReasons['mapper_returned_null'] = (stats.skipReasons['mapper_returned_null'] || 0) + 1;
            return null;
          }
        } catch (err) {
          stats.totalSkipped++;
          stats.skipReasons['exception'] = (stats.skipReasons['exception'] || 0) + 1;
          if (debug) {
            console.error('[CalendarPage] Error mapping reservation to calendar event:', {
              id: reservation.reservation_id,
              error: err.message,
              stack: err.stack,
            });
          }
          return null;
        }
      })
      .filter(event => event !== null);

    // PHASE 2: Debug-only summary
    if (debug) {
      console.log('[CalendarPage] Calendar mapping stats:', stats);
    }

    // Store stats for debug display (if needed)
    if (debug && typeof window !== 'undefined') {
      window.__calendarMappingStats = stats;
    }

    return events;
  }, [mappedReservations, debug]);

  const calendarBookingsWithPending = useMemo(() => {
    if (!pendingOwnerStays.length && Object.keys(ownerStayLocalChanges).length === 0) {
      return calendar_bookings;
    }

    const canonicalIds = new Set();
    const seenIds = new Set();
    const mergedBookings = [];

    calendar_bookings.forEach((booking) => {
      [
        booking?.reservation_id,
        booking?.hostaway_reservation_id,
        booking?.id,
      ].forEach((value) => {
        if (value !== null && value !== undefined && value !== '') {
          canonicalIds.add(String(value));
        }
      });

      const key = getOwnerStayReservationKey(booking);
      const change = key ? ownerStayLocalChanges[key] : null;
      if (change?.type === 'cancel') {
        return;
      }

      const useOverride =
        change?.type === 'upsert' &&
        !ownerStayOverrideMatchesSyncedBooking(booking, change.booking);
      const bookingToUse = useOverride ? change.booking : booking;
      const finalKey = getOwnerStayReservationKey(bookingToUse);
      if (finalKey) seenIds.add(finalKey);
      mergedBookings.push(bookingToUse);
    });

    const activePending = pendingOwnerStays.filter((stay) => {
      if (Number(stay.listing_id) !== Number(listingId)) return false;
      const key = getOwnerStayReservationKey(stay);
      if (key && ownerStayLocalChanges[key]?.type === 'cancel') return false;
      if (key && ownerStayLocalChanges[key]?.type === 'upsert') return false;
      const hostawayId = stay.hostaway_reservation_id;
      return !hostawayId || !canonicalIds.has(String(hostawayId));
    });

    const detachedOverrides = Object.entries(ownerStayLocalChanges)
      .filter(([, change]) => change?.type === 'upsert')
      .map(([key, change]) => ({ key, booking: change.booking }))
      .filter(({ key, booking }) => (
        !seenIds.has(key) &&
        Number(booking?.listing_id) === Number(listingId)
      ))
      .map(({ booking }) => booking);

    return [...mergedBookings, ...activePending, ...detachedOverrides];
  }, [calendar_bookings, ownerStayLocalChanges, pendingOwnerStays, listingId]);

  // Progressive rendering: Show header immediately, even if data is loading
  // Only show error/empty states for specific conditions
  const showReservationsError = reservationsError && !reservationsLoading;
  const hasProperty = !!selectedProperty;

  // Filter calendar bookings by status bucket (exclude cancelled/inquiry by default)
  // Default: show BOOKED + OWNER_STAY, exclude CANCELLED + INQUIRY
  const statusFilteredBookings = calendarBookingsWithPending.filter(b => {
    const bucket = getStatusBucket(b.status);
    // Include BOOKED and OWNER_STAY, exclude CANCELLED and INQUIRY
    return bucket === 'BOOKED' || bucket === 'OWNER_STAY';
  });

  // Filter upcoming events based on toggle (use statusFilteredBookings)
  const filteredEvents = statusFilteredBookings.filter(b => {
    // Use parseLocalDate to avoid timezone shifting bug (new Date("YYYY-MM-DD") parses as UTC midnight)
    const checkIn = parseLocalDate(b.check_in_date);
    const checkOut = parseLocalDate(b.check_out_date);
    if (!checkIn || !checkOut) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    if (eventFilter.checkIn && checkIn >= today && checkIn < nextWeek) return true;
    if (eventFilter.checkOut && checkOut >= today && checkOut < nextWeek) return true;
    return false;
  });

  // TEMP DEBUG: Log counts at each stage (gated by ?perf_debug=1)
  const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';
  
  // CRITICAL FIX: Filter bookings by month overlap BEFORE passing to calendar
  // The calendar should only show bookings that overlap the selected month
  // Booking overlaps month if: checkIn <= monthEnd AND checkOut >= monthStart
  // This ensures reservations spanning month boundaries show pills in both months
  const monthOverlapFilteredBookings = statusFilteredBookings.filter(b => {
    if (!b.check_in_date || !b.check_out_date) return false;
    // Use parseLocalDate to avoid timezone shifting bug (new Date("YYYY-MM-DD") parses as UTC midnight)
    const checkIn = parseLocalDate(b.check_in_date);
    const checkOut = parseLocalDate(b.check_out_date);
    if (!checkIn || !checkOut) return false;
    // Booking overlaps month if checkin <= monthEnd AND checkout >= monthStart
    // Note: checkIn and checkOut are at local midnight (00:00:00)
    // monthStart is at local midnight (00:00:00), monthEnd is at 23:59:59.999
    // Using <= and >= ensures bookings that start/end exactly on month boundaries are included in both months
    const overlaps = checkIn <= monthEnd && checkOut >= monthStart;
    
    // TEMP DEBUG: Log sample booking details (first booking only)
    if (perfDebug && statusFilteredBookings.indexOf(b) === 0) {
      console.log('[CalendarPage] TEMP DEBUG - Month overlap filter sample (first booking):', {
        reservation_id: b.reservation_id,
        raw_check_in_date: b.check_in_date,
        raw_check_out_date: b.check_out_date,
        parsed_checkIn: checkIn.toISOString(),
        parsed_checkOut: checkOut.toISOString(),
        monthStart: monthStart.toISOString(),
        monthEnd: monthEnd.toISOString(),
        checkIn_getTime: checkIn.getTime(),
        monthEnd_getTime: monthEnd.getTime(),
        checkOut_getTime: checkOut.getTime(),
        monthStart_getTime: monthStart.getTime(),
        checkIn_lt_monthEnd: checkIn < monthEnd,
        checkOut_gt_monthStart: checkOut > monthStart,
        overlaps: overlaps,
      });
    }
    
    return overlaps;
  });

  const handleCalendarDateClick = useCallback((date, dateKey) => {
    const clickedDateKey = normalizeDateKey(dateKey, date);

    handleDateClick(date, clickedDateKey || dateKey);

    if (!clickedDateKey || dateHasBooking(statusFilteredBookings, clickedDateKey)) {
      return;
    }

    setOwnerStayModalMode('create');
    setOwnerStayEditBooking(null);
    setOwnerStayDefaultCheckInDate(clickedDateKey);
    setIsOwnerStayModalOpen(true);
  }, [handleDateClick, statusFilteredBookings]);
  
  // Build channels array from bookings using master_channel_* metadata (deduped by master_channel_key)
  // This ensures correct colors/labels match backend canonical mapping
  // MOVED AFTER monthOverlapFilteredBookings declaration to avoid TDZ
  const channels = useMemo(() => {
    if (!monthOverlapFilteredBookings || monthOverlapFilteredBookings.length === 0) {
      return [];
    }
    
    // Extract unique master channels from bookings
    const masterChannelsMap = new Map();
    
    monthOverlapFilteredBookings.forEach(booking => {
      const canonical = booking.canonical_financial_summary;
      const masterKey = canonical?.master_channel_key;
      const masterLabel = canonical?.master_channel_label;
      const masterColor = canonical?.master_channel_color;
      
      // Skip if no master channel metadata
      if (!masterKey) return;
      
      // Dedupe by master_channel_key (ensures "Website/Website" duplicates are eliminated)
      if (!masterChannelsMap.has(masterKey)) {
        masterChannelsMap.set(masterKey, {
          master_channel_key: masterKey,
          master_channel_label: masterLabel || masterKey,
          master_channel_color: masterColor || '#6b7280',
        });
      }
    });
    
    // Convert map to array and sort by master_channel_key for consistent order
    const channelsArray = Array.from(masterChannelsMap.values());
    channelsArray.sort((a, b) => {
      // Sort order: VRBO, Airbnb, CLC, Owner (alphabetical fallback)
      const order = { 'vrbo': 1, 'airbnb': 2, 'clc': 3, 'owner': 4 };
      return (order[a.master_channel_key] || 99) - (order[b.master_channel_key] || 99);
    });
    
    return channelsArray;
  }, [monthOverlapFilteredBookings]);

  // Year dropdown: reservation check-in years only, descending. No synthetic current year.
  const dropdownYears = useMemo(() => [...(reservationYears || [])], [reservationYears]);

  const onApplyLog = useCallback(
    (prev, next) => {
      if (!debug || typeof window === 'undefined') return;
      const fmt = (d) => (d && !isNaN(d.getTime()) ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : null);
      const nextMonth = next ? `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}` : null;
      console.log('[CalendarPage] DEBUG Apply', {
        before: fmt(prev),
        after: fmt(next),
        fetchKeyWillChange: nextMonth ? `listingId-${next?.getFullYear()}-${(next?.getMonth() ?? 0) + 1}` : null,
        expectRefetch: !!nextMonth,
      });
    },
    [debug]
  );

  // Filter upcoming events based on toggle (use statusFilteredBookings)
  const upcomingEvents = statusFilteredBookings.filter(b => {
    // Use parseLocalDate to avoid timezone shifting bug (new Date("YYYY-MM-DD") parses as UTC midnight)
    const checkIn = parseLocalDate(b.check_in_date);
    const checkOut = parseLocalDate(b.check_out_date);
    if (!checkIn || !checkOut) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    if (eventFilter.checkIn && checkIn >= today && checkIn < nextWeek) return true;
    if (eventFilter.checkOut && checkOut >= today && checkOut < nextWeek) return true;
    return false;
  });
  
  // Filter calendar bookings by month for KPI calculations (same filter, but keep separate for clarity)
  // Month summary: data-driven from calendar reservations (occupied nights, owner stays, payout prorated, tourist tax)
  const monthSummary = useMemo(() => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    return computeCalendarMonthSummary({
      year: y,
      monthIndex: m,
      reservations: monthOverlapFilteredBookings ?? [],
    });
  }, [calendarMonth, monthOverlapFilteredBookings]);

  // Single debug log for month summary (?debug=1)
  useEffect(() => {
    if (!debugEnabled) return;
    console.log('[CalendarMonthSummary]', monthSummary);
  }, [debugEnabled, monthSummary]);

  // KPI cards: from month summary (reservations-only). Card order: Nights Booked, Owner Stays, Occupancy, Owner Payout.
  // Tooltips: Owner Payout = check-in month attribution + 3–7 day delay; Owner Stays = % of occupied nights (not total days).
  const OWNER_PAYOUT_TOOLTIP = 'Owner Payout\n\nTotal payout is attributed to the month in which the guest checks in. Please note: actual payout is typically received 3–7 days after check-in.';
  const OWNER_STAYS_TOOLTIP = 'Owner Stays\n\nPercentage reflects the share of occupied nights used by the owner during this month. It does not include vacant days.';

  const kpiCards = useMemo(() => {
    if (reservationsLoading) {
      return [
        { label: 'Nights Booked', value: '...' },
        { label: 'Owner Stays', value: '...' },
        { label: 'Occupancy', value: '...' },
        { label: 'Owner Payout', value: '...' },
      ];
    }
    const s = monthSummary;
    const occupancyPct = Math.min(100, Math.max(0, s.occupancyPct));
    const ownerStayPct = Math.min(100, Math.max(0, s.ownerStayPct));
    return [
      {
        label: 'Nights Booked',
        value: formatNightsBooked(s.occupiedNights, s.totalDaysInMonth),
      },
      {
        label: 'Owner Stays',
        value: `${s.ownerStayNights} days (${ownerStayPct.toFixed(0)}%)`,
        tooltip: OWNER_STAYS_TOOLTIP,
      },
      {
        label: 'Occupancy',
        value: `${occupancyPct.toFixed(1)}%`,
      },
      {
        label: 'Owner Payout',
        value: formatCurrency(s.ownerPayoutProrated),
        tooltip: OWNER_PAYOUT_TOOLTIP,
      },
    ];
  }, [reservationsLoading, monthSummary]);

  // Use memoized display values
  const propertyShortName = propertyDisplayValues.shortName;
  const propertyHero = propertyDisplayValues.hero;
  const propertyThumb = propertyDisplayValues.thumb;
  
  // PHASE 5: Get month/year label for command bar
  const monthYearLabel = useMemo(() => {
    return calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [calendarMonth]);
  
  // PHASE 10: Mobile Runtime Debug Overlay (gated by ?perf_debug=1)
  const showDebugOverlay = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).get('perf_debug') === '1' && 
    isMobile;
  
  return (
    <div className="owner-dashboard-container" style={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent' }}>
      {/* PHASE 10: Mobile Runtime Debug Overlay */}
      {showDebugOverlay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: '#fff',
          padding: '8px 12px',
          fontSize: '11px',
          fontFamily: 'monospace',
          zIndex: 9999,
          lineHeight: '1.4',
          maxHeight: '50vh',
          overflowY: 'auto',
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>📱 Mobile Runtime Debug</div>
          <div>Viewport: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'}</div>
          <div>Device Pixel Ratio: {typeof window !== 'undefined' ? window.devicePixelRatio : 'N/A'}</div>
          <div>CSS Media Query (max-width: 768px): {typeof window !== 'undefined' ? (window.matchMedia('(max-width: 768px)').matches ? '✅ true' : '❌ false') : 'N/A'}</div>
          <div>JS isMobile: {isMobile ? '✅ true' : '❌ false'}</div>
          <div>Listing ID: {listingId || 'null'}</div>
          <div>Components: MonthGrid ✅, WeekStrip {selectedWeekStart ? '✅' : '❌'}, DayDetails {selectedDate ? '✅' : '❌'}</div>
          <div>JS Bundle: {typeof document !== 'undefined' ? (() => {
            const script = document.querySelector('script[src*="/assets/main-"]');
            return script ? script.src.split('/').pop() : 'N/A';
          })() : 'N/A'}</div>
          <div>CSS Bundle: {typeof document !== 'undefined' ? (() => {
            const link = document.querySelector('link[href*="/assets/main-"]');
            return link ? link.href.split('/').pop() : 'N/A';
          })() : 'N/A'}</div>
        </div>
      )}
      
      {/* Hero Backdrop - Fixed background layer that persists behind content */}
      {/* Render immediately with premium fallback, swap hero when loaded */}
      <div style={{
        ...styles.getHeroBackdropStyle(isMobile),
        backgroundImage: (propertyHero && heroImageReady) 
          ? `url(${propertyHero})` 
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)', // Premium neutral gradient fallback
        transition: 'background-image 0.3s ease',
      }}>
        {propertyHero && heroImageReady && <div style={styles.heroOverlay} />}
        {/* Fade overlay: transitions hero to neutral background */}
        <div style={styles.heroBackdropFade} />
      </div>
      
      {/* Dock Sentinel - Invisible element positioned at top of hero backdrop to trigger sticky header docking */}
      {/* When sentinel scrolls out of view (above viewport), header docks */}
      <div 
        ref={dockSentinelRef} 
        style={{ 
          position: 'absolute', 
          top: isMobile ? 'var(--spacing-3)' : 'var(--spacing-4)', // Match header marginTop when undocked
          left: 0, 
          width: '1px', 
          height: '1px', 
          pointerEvents: 'none', 
          visibility: 'hidden',
          zIndex: 1,
        }} 
      />
      
      {/* PHASE 5: Mobile Command Bar - Sticky below global nav, mobile-only */}
      <CalendarPageMobileCommandBar
        isMobile={isMobile}
        hasProperty={hasProperty}
        monthYearLabel={monthYearLabel}
        handlePrevMonth={handlePrevMonth}
        handleNextMonth={handleNextMonth}
        handleToday={handleToday}
        styles={styles}
      />
      
      {/* Header Card - Scroll-activated sticky, overlaps backdrop when undocked */}
      {/* PHASE 2: Hide header card on mobile (property context is in shell header chip) */}
      {/* FIX: Hide duplicate property header on mobile - OwnerLayout already shows property chip in header */}
      <CalendarPageHeader
        isMobile={isMobile}
        isDocked={isDocked}
        hasProperty={hasProperty}
        propertiesLoading={propertiesLoading}
        propertyShortName={propertyShortName}
        propertyThumb={propertyThumb}
        selectedProperty={selectedProperty}
        calendarMonth={calendarMonth}
        setCalendarMonth={setCalendarMonth}
        setIsPropertyOverviewOpen={setIsPropertyOverviewOpen}
        styles={styles}
        yearOptions={dropdownYears}
        debug={debug}
        onApply={onApplyLog}
      />

      {/* Error states (reservations error, no property, timeout) */}
      <CalendarPageErrorStates
        isMobile={isMobile}
        showReservationsError={showReservationsError}
        reservationsError={reservationsError}
        hasProperty={hasProperty}
        propertiesLoading={propertiesLoading}
        propertiesError={propertiesError}
        availableProperties={availableProperties}
        showPropertiesTimeout={showPropertiesTimeout}
        retryProperties={retryProperties}
        styles={styles}
      />

      {/* PHASE 8: Mobile Week Focus Strip - Shows selected week below command bar */}
      {isMobile && selectedWeekStart && (
        <MobileWeekStrip
          weekStart={selectedWeekStart}
          selectedDate={selectedDate}
          bookings={monthOverlapFilteredBookings}
          onDayClick={handleCalendarDateClick}
          isMobile={isMobile}
        />
      )}

      {/* Calendar Module - Transparent shell to show hero, calendar card remains white */}
      {/* Show skeleton while loading, calendar when ready */}
      <div style={{ ...styles.getCalendarPanelStyle(isMobile), backgroundColor: 'transparent', boxShadow: 'none', border: 'none' }}>
        {reservationsLoading || !hasProperty ? (
          <div style={styles.calendarSkeleton}>
            <SkeletonCard />
            <div style={{ marginTop: 'var(--spacing-4)' }}>
              <SkeletonText lines={3} />
            </div>
          </div>
        ) : (
          <CalendarPageGridWithPerf
            bookings={monthOverlapFilteredBookings}
            channels={channels}
            month={calendarMonth}
            monthStart={monthStart}
            monthEnd={monthEnd}
            onBookingClick={handleBookingClick}
            onDateClick={handleCalendarDateClick}
            onMonthChange={setCalendarMonth}
            isCollapsed={isCalendarCollapsed}
            onToggleCollapse={toggleCalendarCollapse}
            onOwnerBookingClick={handleOpenOwnerStayModal}
            isMobile={isMobile}
            selectedDate={selectedDate}
            mountTimeRef={mountTimeRef}
            bookingsFetchCount={bookingsFetchCount}
            availability={availability}
          />
        )}
      </div>

      {/* PHASE 8: Mobile Day Detail Cards - Shows bookings for selected day */}
      {/* PHASE 9D: Spatial continuity - Smooth appear transition */}
      {isMobile && dayDetailsExpanded && selectedDate && (
        <MobileDayDetails
          selectedDate={selectedDate}
          bookings={monthOverlapFilteredBookings}
          onBookingClick={handleBookingClick}
          isMobile={isMobile}
          onCollapseDetails={() => setDayDetailsExpanded(false)}
        />
      )}

      {/* KPI Cards - PHASE 7A: Mobile highlights with expand/collapse */}
      <CalendarPageKPICards
        isMobile={isMobile}
        calendarMetricsError={false}
        kpiCards={kpiCards}
        styles={styles}
      />

      {/* Below Calendar: Upcoming Events Snapshot (merged card) - Only show for current month */}
      <div style={styles.getBelowCalendarStyle(isMobile)} className="calendar-below-section">
        <div style={styles.fullWidthColumn}>
          {(() => {
            const today = new Date();
            const isCurrentMonth = calendarMonth.getFullYear() === today.getFullYear() && 
                                   calendarMonth.getMonth() === today.getMonth();
            return isCurrentMonth ? (
              <UpcomingEventsSnapshot 
                snapshot={next7DaysSnapshot}
                loading={next7DaysSnapshotLoading}
                error={next7DaysSnapshotError}
                isMobile={isMobile}
              />
            ) : null;
          })()}
          
          {/* Tourist taxes due this month banner */}
          <CalendarPageTouristTaxBanner
            isMobile={isMobile}
            monthlyTouristTaxSum={monthSummary?.touristTaxesDue ?? 0}
            navigate={navigate}
            styles={styles}
          />
        </div>
      </div>

      <BookingDetailModal
        isOpen={isModalOpen}
        booking={selectedBooking ? normalizeBookingForModal(sanitizeForOwnerView(selectedBooking, effectiveMode), selectedProperty?.listing_id) : null}
        onClose={handleCloseModal}
        onEditOwnerStay={handleEditOwnerStay}
        onCancelOwnerStay={handleCancelOwnerStay}
      />
      
      <PropertyOverviewModal
        isOpen={isPropertyOverviewOpen}
        property={selectedProperty}
        onClose={() => setIsPropertyOverviewOpen(false)}
      />

      <OwnerStayBookingModal
        isOpen={isOwnerStayModalOpen}
        property={selectedProperty}
        defaultCheckInDate={defaultOwnerStayCheckIn}
        accountDefaults={ownerAccountDefaults}
        mode={ownerStayModalMode}
        initialStay={ownerStayEditBooking}
        availability={availability}
        onClose={handleOwnerStayModalClose}
        onSubmit={handleOwnerStaySubmit}
      />
      
    </div>
  );
}

// PHASE 6A: Mobile Property Switcher (property selection list)
// OLD CODE REMOVED - Nested components extracted to separate files (Phase 4)
// - MobilePropertySwitcher -> CalendarPageMobilePropertySwitcher.jsx
// - MobilePropertyLinksList -> CalendarPageMobilePropertyLinksList.jsx
// - CalendarGridWithPerf -> CalendarPageGridWithPerf.jsx
// OLD CODE REMOVED - Styles object extracted to calendarPageStyles.js (Phase 6)
// The styles object has been moved to frontend/src/styles/calendarPageStyles.js
// All style values moved verbatim to preserve visual behavior exactly.
